import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { Package, Pencil, Eye, EyeOff, Trash2, Archive, FileText, Link2, Check } from 'lucide-react';
import { apiClient as api } from '../../services/api';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface ProductVariant {
  variant_id: string;
  price: number;
  currency: string;
  billing_type: string;
  label: string;
  active: boolean;
}

interface Product {
  product_id: string;
  name: string;
  category: string;
  product_type: string;
  status: 'draft' | 'active' | 'archived';
  cover_image_url?: string;
  variants: ProductVariant[];
  file_count: number;
  order_count: number;
  created_at: string;
  /** Returned by GET /api/products/ (list_products' serializer includes it
   *  alongside whitepaper_gated / generation_status) -- lets the
   *  "Whitepaper" badge below render without a per-product detail fetch. */
  source_task_id?: string | null;
  /** Stable, non-expiring public URL (product_routes._product_share_url) --
   *  for pasting into a Meta Lead Form "Go to website" field, or sharing as
   *  a direct product/checkout link. Null when nothing eligible exists yet
   *  (e.g. a whitepaper still gated, or an uploaded product with no free/
   *  published variant) -- see the hint rendered below when absent. */
  share_url?: string | null;
}

type StatusFilter = 'all' | 'draft' | 'active' | 'archived';

const CATEGORY_LABELS: Record<string, string> = {
  ebook: 'eBook',
  template: 'Template',
  software: 'Software',
  course: 'Course',
  physical: 'Physical',
  other: 'Other',
};

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  download: 'Download',
  license_key: 'License Key',
  access: 'Access',
  lead_magnet: 'Lead Magnet',
  physical: 'Physical',
};

interface ProductsListProps {
  /** Bumping this re-fetches the product list — used by ProductsModule after
   *  a whitepaper generate/regenerate so the new product shows up. */
  refreshKey?: number;
  onNewWhitepaper?: () => void;
}

const ProductsList: React.FC<ProductsListProps> = ({ refreshKey, onNewWhitepaper }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [copiedProductId, setCopiedProductId] = useState<string | null>(null);

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    if (refreshKey) loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const loadProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/products/');
      setProducts(res.data);
    } catch (err: any) {
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePublishToggle = async (product: Product) => {
    setActionLoading(product.product_id);
    try {
      if (product.status === 'active') {
        await api.post(`/products/${product.product_id}/unpublish`);
        setProducts(prev =>
          prev.map(p =>
            p.product_id === product.product_id ? { ...p, status: 'draft' } : p
          )
        );
      } else {
        await api.post(`/products/${product.product_id}/publish`);
        setProducts(prev =>
          prev.map(p =>
            p.product_id === product.product_id ? { ...p, status: 'active' } : p
          )
        );
      }
    } catch (err: any) {
      setError(getApiError(err, 'Action failed. Please try again.'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchive = async (product: Product) => {
    if (!window.confirm(`Archive "${product.name}"? It will be hidden from buyers but can be restored later.`)) return;
    setActionLoading(product.product_id);
    try {
      await api.post(`/products/${product.product_id}/archive`);
      setProducts(prev =>
        prev.map(p =>
          p.product_id === product.product_id ? { ...p, status: 'archived' } : p
        )
      );
    } catch (err: any) {
      setError(getApiError(err, 'Failed to archive product.'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (product: Product) => {
    if (!window.confirm(`Permanently delete "${product.name}"? This cannot be undone.`)) return;
    setActionLoading(product.product_id);
    try {
      await api.delete(`/products/${product.product_id}`);
      setProducts(prev => prev.filter(p => p.product_id !== product.product_id));
    } catch (err: any) {
      setError(getApiError(err, 'Failed to delete product.'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopyShareLink = (product: Product) => {
    if (!product.share_url) return;
    navigator.clipboard.writeText(product.share_url).then(() => {
      setCopiedProductId(product.product_id);
      setTimeout(() => setCopiedProductId(null), 2000);
    });
  };

  const shareLinkUnavailableReason = (product: Product): string => {
    if (product.source_task_id) {
      return 'Publish this whitepaper and turn off email gating to get a shareable link.';
    }
    return 'Add a free (or Stripe-published) price variant to get a shareable link.';
  };

  const getPriceLabel = (product: Product): React.ReactNode => {
    if (product.product_type === 'lead_magnet') {
      return <span className="product-price-tag free">Free</span>;
    }
    if (!product.variants || product.variants.length === 0) {
      return <span className="product-price-tag" style={{ color: 'var(--text-muted)' }}>No price set</span>;
    }
    const prices = product.variants.map(v => v.price);
    const minPrice = Math.min(...prices);
    const currencySymbols: Record<string, string> = { USD: '$', GBP: '£', EUR: '€', AUD: 'A$' };
    const currency = product.variants[0]?.currency || 'USD';
    const symbol = currencySymbols[currency] || currency + ' ';
    if (prices.length === 1) {
      return <span className="product-price-tag">{minPrice === 0 ? 'Free' : `${symbol}${minPrice.toFixed(2)}`}</span>;
    }
    return <span className="product-price-tag">From {symbol}{minPrice.toFixed(2)}</span>;
  };

  const filtered = products.filter(p =>
    statusFilter === 'all' ? p.status !== 'archived' : p.status === statusFilter
  );

  const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'draft', label: 'Draft' },
    { key: 'active', label: 'Active' },
    { key: 'archived', label: 'Archived' },
  ];

  if (loading) {
    return <div className="products-loading">Loading products...</div>;
  }

  return (
    <div>
      {error && <div className="products-error">{error}</div>}

      <div className="products-filter-bar">
        {STATUS_FILTERS.map(f => (
          <button
            key={f.key}
            className={`filter-chip${statusFilter === f.key ? ' active' : ''}`}
            onClick={() => setStatusFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="products-empty-state">
          <Package size={48} />
          <p>
            {products.filter(p => p.status !== 'archived').length === 0 && statusFilter === 'all'
              ? 'No products yet. Create your first product to start selling.'
              : 'No products match your filter.'}
          </p>
          {products.length === 0 && (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <GlassButton variant="primary" onClick={() => navigate('/products/new')}>
                Create First Product
              </GlassButton>
              {onNewWhitepaper && (
                <GlassButton variant="secondary" icon={<FileText size={16} />} onClick={onNewWhitepaper}>
                  New Whitepaper
                </GlassButton>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="product-grid">
          {filtered.map(product => (
            <GlassCard key={product.product_id} variant="default" padding="md" className="product-card">
              <div className="product-card-cover">
                {product.cover_image_url ? (
                  <img src={product.cover_image_url} alt={product.name} />
                ) : (
                  <div className="product-card-cover-placeholder">
                    <Package size={48} />
                  </div>
                )}
              </div>

              <div className="product-card-body">
                <h3 className="product-card-title">{product.name}</h3>

                <div className="product-card-meta">
                  {product.category && (
                    <span className="product-category-badge">
                      {CATEGORY_LABELS[product.category] || product.category}
                    </span>
                  )}
                  {product.product_type && PRODUCT_TYPE_LABELS[product.product_type] && product.product_type !== 'download' && (
                    <span className="product-category-badge" style={{ opacity: 0.8 }}>
                      {PRODUCT_TYPE_LABELS[product.product_type]}
                    </span>
                  )}
                  {product.source_task_id && (
                    <span className="whitepaper-badge" title="Generated from an article">
                      <FileText size={12} /> Whitepaper
                    </span>
                  )}
                  <span className={`product-status-badge ${product.status}`}>
                    {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {getPriceLabel(product)}
                  <span className="product-sales-count">{product.order_count} sale{product.order_count !== 1 ? 's' : ''}</span>
                </div>

                <div className="product-card-footer">
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    icon={<Pencil size={14} />}
                    onClick={() => navigate(`/products/${product.product_id}`)}
                    title="Edit product"
                  >
                    Edit
                  </GlassButton>

                  {product.share_url ? (
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      icon={copiedProductId === product.product_id ? <Check size={14} /> : <Link2 size={14} />}
                      onClick={() => handleCopyShareLink(product)}
                      title="Copy shareable link (e.g. for a Meta Lead Form's 'Go to website' field)"
                    >
                      {copiedProductId === product.product_id ? 'Copied' : 'Copy Link'}
                    </GlassButton>
                  ) : (
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      icon={<Link2 size={14} />}
                      disabled
                      title={shareLinkUnavailableReason(product)}
                    >
                      Copy Link
                    </GlassButton>
                  )}

                  <GlassButton
                    variant="secondary"
                    size="sm"
                    icon={product.status === 'active' ? <EyeOff size={14} /> : <Eye size={14} />}
                    onClick={() => handlePublishToggle(product)}
                    disabled={actionLoading === product.product_id || product.status === 'archived'}
                    title={product.status === 'active' ? 'Unpublish' : 'Publish'}
                  >
                    {product.status === 'active' ? 'Unpublish' : 'Publish'}
                  </GlassButton>

                  {product.status !== 'archived' && (
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      icon={<Archive size={14} />}
                      onClick={() => handleArchive(product)}
                      disabled={actionLoading === product.product_id}
                      title="Archive product"
                    />
                  )}

                  <GlassButton
                    variant="danger"
                    size="sm"
                    icon={<Trash2 size={14} />}
                    onClick={() => handleDelete(product)}
                    disabled={actionLoading === product.product_id}
                    title="Delete product permanently"
                  />
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductsList;
